import { URL_API } from "@/config/domains";
import type {
  Gateways,
  GatewayEventMessage,
  GatewayRequest,
  GatewayResponse,
  GatewayTopic,
} from "@/@types/apiTypes/Gateways";
import { useGatewayDebuggerStore } from "@/Hub/Views/GatewayDebuggerView/stores/gatewayDebuggerStore";

type GatewayStatus = "disconnected" | "connecting" | "connected" | "error";

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timeout?: number;
  startTime?: number;
  method?: string;
  params?: any;
};

type APIEndpoint = {
  response: any;
} & ({ request: any } | { query: any });

type APIRequestMethod = {
  [K in keyof API]: API[K] extends APIEndpoint ? K : never;
}[keyof API];

type APIRequest<T extends APIRequestMethod> = Extract<
  API[T],
  APIEndpoint
> extends { request: infer Request }
  ? Request
  : Extract<API[T], APIEndpoint> extends { query: infer Query }
    ? Query
    : never;

type APIResponse<T extends APIRequestMethod> = Extract<
  API[T],
  APIEndpoint
>["response"];

type EventCallback = (data: any, filter: any) => void;

type Subscription = {
  topic: GatewayTopic;
  filter: any;
};

const isClient = typeof window !== "undefined";
const REQUEST_TIMEOUT_MS = 15000;

const getGatewayUrl = () => {
  const wsBase = URL_API.replace(/^http/, "ws");
  return `${wsBase}/gateway`;
};

const stableStringify = (value: any): string => {
  if (value === undefined || value === null) return "null";
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(",")}]`;
  }
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
    .join(",")}}`;
};

const buildSubKey = (topic: string, filter: any) => {
  return `${topic}:${stableStringify(filter)}`;
};

const createRequestId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

class Gateway {
  status: GatewayStatus = "disconnected";
  preferGateway = false;
  memberRef: string | null = null; // User's identity (mbXXX)

  private ws: WebSocket | null = null;
  private pending = new Map<string, PendingRequest>();
  private listeners = new Map<string, Map<string, Set<EventCallback>>>();
  private statusListeners = new Set<(status: GatewayStatus) => void>();
  private connectPromise: Promise<void> | null = null;
  private reconnectTimer: number | null = null;
  private shouldReconnect = false;
  private subscriptions = new Map<string, Subscription>();

  get connected() {
    return this.status === "connected";
  }

  get connecting() {
    return this.status === "connecting";
  }

  onStatus(listener: (status: GatewayStatus) => void) {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  private setStatus(status: GatewayStatus) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect() {
    if (!this.shouldReconnect || this.reconnectTimer) return;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect().catch(() => undefined);
    }, 1500);
  }

  private rejectPending(error: any) {
    this.pending.forEach((pending) => pending.reject(error));
    this.pending.clear();
  }

  private send(payload: any) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  private resubscribe() {
    if (!this.connected) return;
    this.subscriptions.forEach((sub) => {
      this.send({ type: "subscribe", topic: sub.topic, filter: sub.filter });
    });
  }

  private handleMessage = (event: MessageEvent) => {
    if (!event.data) return;

    let payload: GatewayResponse | GatewayEventMessage | null = null;
    try {
      payload = JSON.parse(event.data);
    } catch (error) {
      return;
    }

    if (!payload || typeof payload !== "object") return;

    if (payload.type === "response") {
      const pending = this.pending.get(payload.id);
      if (!pending) return;

      if (pending.timeout) window.clearTimeout(pending.timeout);
      this.pending.delete(payload.id);

      // Debug: log action response
      if (pending.method) {
        const [topic, name] = pending.method.split("/");
        const duration = pending.startTime
          ? Date.now() - pending.startTime
          : undefined;
        useGatewayDebuggerStore.getState().addEntry({
          type: "response",
          topic,
          name,
          method: pending.method,
          requestId: payload.id,
          data: { request: pending.params, response: payload.data },
          status: payload.status,
          duration,
          error: payload.status >= 400 ? payload.error : undefined,
        });
      }

      if (payload.status < 200 || payload.status >= 300) {
        pending.reject({
          status: payload.status,
          error: payload.error || "Unknown Gateway Error",
          data: payload.data,
        });
        return;
      }

      pending.resolve(payload.data);
      return;
    }

    if (payload.type === "event") {
      // Special handling for server logs
      if (payload.topic === "debug/apiServer" && payload.event === "log") {
        const logData = payload.data as {
          timestamp: number;
          segment: string;
          level: string;
          message: string;
          data?: any;
        };
        useGatewayDebuggerStore.getState().addEntry({
          type: "serverLog",
          topic: payload.topic,
          name: logData.message,
          segment: logData.segment,
          level: logData.level,
          data: logData.data,
        });
      } else {
        // Debug: log event
        useGatewayDebuggerStore.getState().addEntry({
          type: "event",
          topic: payload.topic,
          name: payload.event,
          data: { payload: payload.data, filter: payload.filter },
        });
      }

      const topicListeners = this.listeners.get(payload.topic);
      const eventListeners = topicListeners?.get(payload.event);
      if (!eventListeners || eventListeners.size === 0) return;
      eventListeners.forEach((listener) =>
        listener(payload.data, payload.filter),
      );
    }
  };

  private handleClose = () => {
    this.ws = null;
    this.connectPromise = null;
    this.setStatus("disconnected");
    this.rejectPending({ status: 503, error: "Gateway disconnected" });
    this.scheduleReconnect();
  };

  private handleError = () => {
    this.setStatus("error");
  };

  connect() {
    if (!isClient) return Promise.resolve();
    if (this.connected) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.shouldReconnect = true;
    this.clearReconnectTimer();
    this.setStatus("connecting");

    this.connectPromise = new Promise((resolve, reject) => {
      const ws = new WebSocket(getGatewayUrl());
      this.ws = ws;

      const handleOpen = () => {
        ws.removeEventListener("open", handleOpen);
        this.setStatus("connected");
        this.connectPromise = null;
        this.resubscribe();
        resolve();
      };

      const handleError = () => {
        ws.removeEventListener("error", handleError);
        this.setStatus("error");
        this.connectPromise = null;
        reject(new Error("Gateway connection failed"));
      };

      ws.addEventListener("open", handleOpen, { once: true });
      ws.addEventListener("error", handleError);
      ws.addEventListener("message", this.handleMessage);
      ws.addEventListener("close", this.handleClose);
    });

    return this.connectPromise;
  }

  disconnect() {
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    this.connectPromise = null;
    if (this.ws) {
      this.ws.removeEventListener("message", this.handleMessage);
      this.ws.removeEventListener("close", this.handleClose);
      this.ws.removeEventListener("error", this.handleError);
      this.ws.close();
      this.ws = null;
    }
    this.setStatus("disconnected");
  }

  setMemberRef(memberRef: string | null) {
    this.memberRef = memberRef;
  }

  request<T extends APIRequestMethod>(method: T, params: APIRequest<T>) {
    if (!this.connected || !this.ws) {
      return Promise.reject({ status: 503, error: "Gateway not connected" });
    }

    const id = createRequestId();
    const payload: GatewayRequest = {
      type: "request",
      id,
      method: method as string,
      params,
    };

    return new Promise<APIResponse<T>>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pending.delete(id);
        reject({ status: 504, error: "Gateway timeout" });
      }, REQUEST_TIMEOUT_MS);

      this.pending.set(id, {
        resolve,
        reject,
        timeout,
        startTime: Date.now(),
        method: method as string,
        params,
      });
      this.send(payload);
    });
  }

  action<
    T extends GatewayTopic,
    A extends keyof (Gateways[T] extends { actions: infer Acts }
      ? Acts
      : never),
  >(
    topic: T,
    actionName: A,
    query: (Gateways[T] extends { actions: infer Acts }
      ? Acts
      : never)[A] extends {
      query: infer Q;
    }
      ? Q
      : never,
  ) {
    if (!this.connected || !this.ws) {
      return Promise.reject({ status: 503, error: "Gateway not connected" });
    }

    const method = `${topic as string}/${actionName as string}`;
    const id = createRequestId();
    const payload: GatewayRequest = {
      type: "request",
      id,
      method,
      params: query,
    };

    return new Promise<
      (Gateways[T] extends { actions: infer Acts } ? Acts : never)[A] extends {
        response: infer R;
      }
        ? R
        : never
    >((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pending.delete(id);
        reject({ status: 504, error: "Gateway timeout" });
      }, REQUEST_TIMEOUT_MS);

      this.pending.set(id, {
        resolve,
        reject,
        timeout,
        startTime: Date.now(),
        method,
        params: query,
      });

      // Debug: log request when sending
      const [topic_str, name] = method.split("/");
      useGatewayDebuggerStore.getState().addEntry({
        type: "request",
        topic: topic_str,
        name,
        method,
        requestId: id,
        data: query,
      });

      this.send(payload);
    });
  }

  subscribe<T extends GatewayTopic>(topic: T, filter: any) {
    const key = buildSubKey(topic, filter);
    if (this.subscriptions.has(key)) return;
    this.subscriptions.set(key, { topic, filter });
    this.send({ type: "subscribe", topic, filter });

    // Debug: log subscribe
    useGatewayDebuggerStore.getState().addEntry({
      type: "subscribe",
      topic: topic as string,
      name: "subscribe",
      data: { filter },
    });
  }

  unsubscribe<T extends GatewayTopic>(topic: T, filter: any) {
    const key = buildSubKey(topic, filter);
    this.subscriptions.delete(key);
    this.send({ type: "unsubscribe", topic, filter });

    // Debug: log unsubscribe
    useGatewayDebuggerStore.getState().addEntry({
      type: "unsubscribe",
      topic: topic as string,
      name: "unsubscribe",
      data: { filter },
    });
  }

  on<T extends GatewayTopic, E extends keyof Gateways[T]["events"]>(
    topic: T,
    event: E,
    callback: (data: Gateways[T]["events"][E], filter: any) => void,
  ) {
    const topicKey = topic as string;
    const eventKey = event as string;
    let topicListeners = this.listeners.get(topicKey);
    if (!topicListeners) {
      topicListeners = new Map();
      this.listeners.set(topicKey, topicListeners);
    }
    let eventListeners = topicListeners.get(eventKey);
    if (!eventListeners) {
      eventListeners = new Set();
      topicListeners.set(eventKey, eventListeners);
    }
    eventListeners.add(callback as EventCallback);

    return () => this.off(topic, event, callback);
  }

  off<T extends GatewayTopic, E extends keyof Gateways[T]["events"]>(
    topic: T,
    event: E,
    callback: (data: Gateways[T]["events"][E], filter: any) => void,
  ) {
    const topicListeners = this.listeners.get(topic as string);
    const eventListeners = topicListeners?.get(event as string);
    if (!eventListeners) return;
    eventListeners.delete(callback as EventCallback);
  }
}

const gateway = new Gateway();

export default gateway;
